/**
 * Research tree (GDD §1 "Technologischer Forschungsbaum").
 *
 * Effects are implemented where the systems exist today (shield tech in
 * combat, asteroid mining in harvest yield, logistics in merchant tax,
 * astrometrics in comet odds); the rest are stored so fleets/PvP pick them up
 * when those systems land (see docs/ROADMAP.md).
 */

import type { BuildingKey, ResearchKey, ResourceAmounts } from '../types.js';

export interface ResearchDef {
  key: ResearchKey;
  label: string;
  /** Per-level effect, as shown in the UI. */
  effect: string;
  baseCost: ResourceAmounts;
  /**
   * Exponential growth factor. Research deliberately keeps the GDD's classic
   * curve (`base·factor^(l-1)`): its levels are meant to plateau in the tens,
   * where an exponential wall is exactly the right shape. Buildings use the
   * polynomial curve instead — see formulas.ts.
   */
  factor: number;
  maxLevel?: number;
  requires?: Partial<{
    buildings: Partial<Record<BuildingKey, number>>;
    research: Partial<Record<ResearchKey, number>>;
  }>;
  sprite: string;
}

export const RESEARCH: Record<ResearchKey, ResearchDef> = {
  impulseDrive: {
    key: 'impulseDrive',
    label: 'Impulsantrieb',
    effect: 'Erhöht die Grundgeschwindigkeit von Transportern und leichten Einheiten um +10 % pro Stufe.',
    baseCost: { titan: 400, silicate: 200 },
    factor: 1.8,
    requires: { buildings: { researchLab: 1 } },
    sprite: 'tech-impulse',
  },
  warpFold: {
    key: 'warpFold',
    label: 'Warp-Faltung',
    effect: 'Befähigt Großkampfschiffe zu interstellaren Sprüngen und senkt den Isotop-3-Verbrauch.',
    baseCost: { silicate: 2_000, isotope: 1_200 },
    factor: 1.9,
    requires: { buildings: { researchLab: 4 }, research: { impulseDrive: 3 } },
    sprite: 'tech-warp',
  },
  shieldHarmonics: {
    key: 'shieldHarmonics',
    label: 'Schild-Harmonisierung',
    effect: 'Erhöht die Schildkapazität aller Flotten und Verteidigungsanlagen um +10 % pro Stufe.',
    baseCost: { titan: 800, silicate: 1_600 },
    factor: 1.8,
    requires: { buildings: { researchLab: 2 } },
    sprite: 'tech-shield',
  },
  asteroidMining: {
    key: 'asteroidMining',
    label: 'Asteroiden-Bergbau',
    effect: 'Erhöht die Abbaugeschwindigkeit und Ladekapazität auf temporären Himmelskörpern um +15 % pro Stufe.',
    baseCost: { titan: 1_000, silicate: 500 },
    factor: 1.7,
    requires: { buildings: { researchLab: 2 } },
    sprite: 'tech-mining',
  },
  logistics: {
    key: 'logistics',
    label: 'Logistik & Handelsverträge',
    effect: 'Senkt die Marktsteuer im Händler-Portal um 1 % pro Stufe (Basis 15 %, Minimum 3 %).',
    baseCost: { credit: 2_500 },
    factor: 1.6,
    maxLevel: 12,
    requires: { buildings: { tradeRelay: 1 } },
    sprite: 'tech-logistics',
  },
  astrometrics: {
    key: 'astrometrics',
    label: 'Astrometrische Triangulation',
    effect: 'Erhöht die Aufspürchance seltener Kometen und Events um +5 % pro Stufe.',
    baseCost: { silicate: 1_200, isotope: 400 },
    factor: 1.75,
    requires: { buildings: { researchLab: 3 } },
    sprite: 'tech-astrometrics',
  },
  gravitonPhysics: {
    key: 'gravitonPhysics',
    label: 'Graviton-Physik',
    effect: 'Endgame-Technologie. Voraussetzung für Mondzerstörer und Dyson-Sphären.',
    baseCost: { silicate: 300_000, isotope: 200_000, aether: 500 },
    factor: 3,
    requires: { buildings: { researchLab: 12 } },
    sprite: 'tech-graviton',
  },
};

export const RESEARCH_KEYS = Object.keys(RESEARCH) as ResearchKey[];
